import {
  FUTURE_OPTIONS,
  PAST_OPTIONS,
  parseDueDateUIFilter,
} from 'components/ColumnFilterDueDate';
import _, { find, isEmpty, isString, map } from 'lodash';
import moment from 'moment';
import { DATE_FILTER_API_SUPPORT } from 'views/Findings/constants/columns';

import {
  DateRangeQuickOptions,
  getStartAndEndDateForQuickOption,
} from './quickOptions';
import { convertRelativeToAbsolute } from './relative';

export const parseOldDateFormatsToDate = (
  filterKey: string,
  values: (string | number)[],
) => {
  const dueDateKeys = [
    'slaDueDate',
    'triageDueDate',
    'remediationDueDate',
    'dueDate',
  ];
  if (dueDateKeys.includes(filterKey)) {
    if (values.length === 2) {
      // ["10", ""] - 10 days ago till now
      // ["", "10"] - now till next 10 day
      // ["10", "10"] - 10 days ago till next 10 days
      if (
        (Number(values[0]) <= 1000 || values[0] === '') &&
        (Number(values[1]) <= 1000 || values[1] === '')
      ) {
        return parseDueDateUIFilter(values);
      }
    } else if (values.length === 1) {
      return parseDueDateUIFilter(values);
    }
  } else {
    if (values.length === 1) {
      return getStartAndEndDateForQuickOption({
        label: values[0] as string,
        utcString: true,
      });
    }
  }
};

export const parseOldDateFormatsToNew = (
  filterKey: string,
  values: (string | number)[],
): [string, string] | undefined => {
  const dueDateKeys = [
    'slaDueDate',
    'triageDueDate',
    'remediationDueDate',
    'dueDate',
  ];
  if (dueDateKeys.includes(filterKey)) {
    if (values.length === 2) {
      // ["10", ""] - 10 days ago till now
      // ["", "10"] - now till next 10 day
      // ["10", "10"] - 10 days ago till next 10 days
      if (
        (Number(values[0]) <= 1000 || values[0] === '') &&
        (Number(values[1]) <= 1000 || values[1] === '')
      ) {
        return [
          values[0] === '' ? 'now' : values[0] + ' d',
          values[1] === '' ? 'now' : values[1] + ' d+',
        ];
      }
    } else if (values.length === 1) {
      const object = find(PAST_OPTIONS, { value: values[0] }) as
        | { startOffset: number; endOffset: number }
        | undefined;
      if (object) {
        const { startOffset, endOffset } = object;
        return [
          `${Math.abs(startOffset)} d`,
          endOffset === 0 ? 'now' : `${Math.abs(endOffset)} d`,
        ];
      } else {
        const futureObject = find(FUTURE_OPTIONS, { value: values[0] }) as
          | { endOffset: number }
          | undefined;
        if (futureObject) {
          const { endOffset } = futureObject;
          return [
            'now', // all startOffset in future options are 0
            endOffset === 0 ? 'now' : `${Math.abs(endOffset)} d+`, // all end offset are either 0 or future
          ];
        }
      }
    }
  } else {
    if (values.length === 1) {
      const option = find(DateRangeQuickOptions, { label: values[0] });
      if (option) {
        const { start = 0, granularity } = option as {
          start?: number;
          granularity?: string;
        };
        return [`${start} ${granularity?.[0]}`, 'now'];
      }
    }
  }
  return undefined;
};

export const convertDateFilterValueToDate = (
  key: string | undefined,
  values: unknown[],
): (string | null)[] => {
  if (isEmpty(values)) {
    return [];
  }
  const oldToNew = parseOldDateFormatsToNew(
    key as string,
    values as (string | number)[],
  );
  if (!isEmpty(oldToNew)) {
    values = oldToNew as (string | number | null)[];
  }
  return map(values, (value, i) => {
    if (_.isNil(value)) {
      return null;
    }
    let epoch: string | number = value as string | number;
    if (value === 'now') {
      epoch = Date.now();
    } else if (
      isString(value) &&
      // Strict ISO_8601 parsing is used here (instead of moment(value)) so that
      // relative values like "90 d" are rejected immediately without Moment
      // falling back to `new Date(value)`, which triggers a console
      // deprecation warning on every non-ISO/RFC2822 string (ENG-133671).
      !moment(value, moment.ISO_8601, true).isValid()
    ) {
      epoch = convertRelativeToAbsolute(value, i === 0 ? 'start' : 'end');
    }
    return moment(epoch).utc().format();
  });
};

export const getOldFormatToNewDateFilters = (
  filters: Record<string, (string | number)[]>,
  dateFilters: string[] = DATE_FILTER_API_SUPPORT,
): Record<string, (string | number)[]> => {
  dateFilters.forEach((key) => {
    if (!isEmpty(filters[key])) {
      const updatedValues = parseOldDateFormatsToNew(key, filters[key]);
      if (!isEmpty(updatedValues)) {
        filters[key] = updatedValues as (string | number)[];
      }
    }
  });

  return filters;
};
